import { Loading } from '@ui-components/neo.tax';
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useUrlQueryParam } from '@neo.tax/shared';
import { applyEmailVerificationActionCode } from '@neo.tax/shared/third-parties/firebase/firebase.utils';

type VerifyState = 'pending' | 'success' | 'failure';

export const VerifyEmail = (): React.ReactElement => {
  const oobCode = useUrlQueryParam('oobCode');
  const continueUrl = useUrlQueryParam('continueUrl');

  const [status, setStatus] = useState<VerifyState>('pending');
  // The email the verified action code was issued for, used to pre-populate the sign-in form
  // on the post-verification redirect.
  const [verifiedEmail, setVerifiedEmail] = useState<string | undefined>(undefined);
  // React 18 strict-mode invokes this effect twice in dev; guard with a ref
  // so `applyActionCode` (non-idempotent — consumes the oobCode) only fires once.
  const hasAppliedRef = useRef(false);

  useEffect(() => {
    if (!oobCode) {
      setStatus('failure');
      return;
    }
    if (hasAppliedRef.current) {
      return;
    }
    hasAppliedRef.current = true;

    (async (): Promise<void> => {
      try {
        const email = await applyEmailVerificationActionCode(oobCode);
        setVerifiedEmail(email);
        setStatus('success');
      } catch {
        // Any Firebase error (expired, invalid, network) routes the user to the
        // resend flow so they can recover with a new link.
        setStatus('failure');
      }
    })();
  }, [oobCode]);

  if (status === 'pending') {
    return <Loading height={80} />;
  }

  if (status === 'failure') {
    return <Navigate to="/verify-email-sent?reason=expired" replace />;
  }

  // Success: redirect to either the caller-provided continueUrl (e.g. an
  // invitation accept page) or /login?verified=1, which forces a fresh sign-in
  // so the refreshed Firebase ID token carries email_verified: true. Pre-populate
  // the sign-in email when the action code exposed which account was verified.
  const loginFallback = verifiedEmail
    ? `/login?verified=1&prefillEmail=${encodeURIComponent(verifiedEmail)}`
    : '/login?verified=1';
  return <Navigate to={continueUrl ?? loginFallback} replace />;
};
